import type { ErrorHandlingStage } from "./ErrorHandler";
import type { ServiceError } from "./BaseTypes";
import { Store } from "../utils/Store";
import { Queue } from "./queue";

export interface QueuedAction<Content> {
	isLazy: boolean;
	apply(content: Content): void;
	send(
		content: Content | null | undefined,
		success: (arg0: Content | null | undefined) => void,
		error: ErrorHandlingStage,
	): void;
	combine(other: QueuedAction<Content>): QueuedAction<Content> | null | undefined; // should return combined action if possible, otherwise null
}
export type ActionQueueStatus = "UNINIT" | "LOADING" | "PENDING" | "STEADY";
export class ActionQueueStore<Content> extends Store {
	_clientState: Content | null | undefined;
	_serverState: Content | null | undefined;
	_queue!: Queue<QueuedAction<Content>>;
	_actionQueueStatus!: ActionQueueStatus;

	constructor() {
		super();
		this.resetQueue();
	}

	resetQueue() {
		this._actionQueueStatus = "UNINIT";
		this._queue = new Queue();
		this._serverState = null;
		this._clientState = null;
	}

	getStatus(): ActionQueueStatus {
		return this._actionQueueStatus;
	}

	receivedServerState(fetchedState: Content | null | undefined) {
		this._serverState = fetchedState;
		this.recalculateOptimisticState();
		this.runNextAction();
	}

	handleActionSuccess(action: QueuedAction<Content>, fetchedState: Content | null | undefined) {
		if (this._queue.peek() !== action) {
			console.error("Successful action not at beginning of queue!"); // Might already have been dequeued because we got an updated state
		} else {
			this._queue.dequeue();
		}

		this.receivedServerState(fetchedState);
	}

	handleActionError(action: QueuedAction<Content>, next: ErrorHandlingStage | null | undefined, error: ServiceError) {
		console.error("QueueActionStore action error:", action.constructor.name, error);
		// if there's a network problem, we should instead retry (with backoff)
		// throw away the queue
		this._actionQueueStatus = "STEADY";
		this._queue = new Queue();
		this.recalculateOptimisticState();
		this.trigger();

		if (next) {
			next(null, error);
		}
	}

	runNextAction(): void {
		const task = this._queue.peek();

		if (!task || this._queue.items().every((x) => x.isLazy)) {
			this._actionQueueStatus = "STEADY";
			this.trigger();
			return;
		}

		this._actionQueueStatus = "PENDING";
		this.trigger();
		task.send(
			this._serverState,
			this.handleActionSuccess.bind(this, task),
			this.handleActionError.bind(this, task),
		);
	}

	startQueue() {
		if (this._actionQueueStatus === "STEADY") {
			this.runNextAction();
		} else {
			this.trigger();
		}
	}

	queueAction(action: QueuedAction<Content>): void {
		if (this._clientState) {
			action.apply(this._clientState);
		}

		if (this._queue.items().length > 1) {
			// we can't combine with the first queue item since it's probably already been sent, but if there's more...
			const last = this._queue.popBack();

			if (last) {
				const combined = action.combine(last);

				if (combined) {
					action = combined;
				} else {
					// put it back
					this._queue.enqueue(last);
				}
			}
		}

		this._queue.enqueue(action);

		this.startQueue();
	}

	recalculateOptimisticState() {
		this._clientState = structuredClone(this._serverState);

		for (const action of this._queue.items()) {
			if (this._clientState) {
				action.apply(this._clientState);
			}
		}
	}
}
